import type { LiteralUnion } from "type-fest";

import { getLogger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import {
    enrollmentStatusToSaleProgress,
    SaleProgress,
    SalesProvider,
    useIntegratedFlowStore,
    useProductOfferStore,
} from "@expert/sales-tools";

import { getRootDispatcher } from "@/analytics";
import { type HoneycombEnrolledProducts, sendToHoneycomb } from "@/analytics/sendToHoneycomb";
import { endDynatraceAction, startDynatraceAction } from "@/utils/RealUserMonitoringClient";

type EnrollmentStatus = LiteralUnion<"SUCCESS" | "FAILURE" | "DECLINED", string>;

export function updatePrimaryProductSaleProgress({
    status,
    offerId,
    salesProvider,
}: {
    status: EnrollmentStatus;
    offerId: string;
    salesProvider: SalesProvider;
}): void {
    const {
        getPrimaryProducts,
        updateProduct,
        isPrimaryProductInProgress,
        getProducts,
        getProductInfo,
        resolveProductOfferId,
    } = useProductOfferStore.getState();
    const { updateIntegratedStep } = useIntegratedFlowStore.getState();
    const sessionId = useProductOfferStore.getState().getSessionIdForProductId(offerId);
    // An event for a quote a re-check moved the selection off applies to the live offer for that sale.
    // Analytics keep the id the event arrived on, so they still join to the backend's events.
    const liveOfferId = sessionId ? resolveProductOfferId(sessionId, offerId) : offerId;
    const primaryProducts = sessionId ? getPrimaryProducts(sessionId) : [];
    const offersList = primaryProducts.map((p) => p.id);

    const provider = getProvider(salesProvider);

    if (!sessionId || primaryProducts.length <= 0 || !offersList.includes(liveOfferId)) {
        return void getRootDispatcher().dispatchBusinessEvent(`Previous${provider}EnrollmentEventReceived`, {
            productOfferId: offerId,
            enrollmentStatus: status,
        });
    }

    // Case-insensitive: the k8s path passes Cloud API's lowercase sales_status through unchanged.
    const progress = enrollmentStatusToSaleProgress(status);

    if (progress == null) {
        return void getRootDispatcher().dispatchBusinessEvent(`Unknown${provider}EnrollmentEventReceived`, {
            productOfferId: offerId,
            enrollmentStatus: status,
        });
    }

    startDynatraceAction("SaleEnrollment", offerId);
    updateProduct(sessionId, liveOfferId, { saleProgress: progress });

    getLogger({ module: "updatePrimaryProductSaleProgress" }).info(
        { enrollmentStatus: status, productOfferId: offerId, sessionId, salesProvider },
        "Enrollment outcome",
    );

    void getRootDispatcher().dispatchBusinessEvent(`${provider}EnrollmentEventReceived`, {
        productOfferId: offerId,
        enrollmentStatus: status,
        sessionId,
    });

    const products = getProducts(sessionId);
    const enrolledProducts: HoneycombEnrolledProducts[] = products
        .filter((p) => p.saleProgress === SaleProgress.Success)
        .map((p) => {
            return {
                productSku: p.productSku,
                productOfferId: p.id,
                enrollmentStatus: p.saleProgress,
                statusOrigin: p.salesProvider,
                displayName: getProductInfo(sessionId, p.productSku)?.displayName,
            };
        });

    const enrolledProductSku = products.filter((p) => p.id === liveOfferId).at(0)?.productSku;
    const newEnrollment: HoneycombEnrolledProducts = {
        enrollmentStatus: progress,
        productSku: enrolledProductSku ?? "",
        productOfferId: offerId,
        statusOrigin: salesProvider,
        displayName: getProductInfo(sessionId, enrolledProductSku)?.displayName,
    };
    const honeycombEnrollment: HoneycombEnrolledProducts[] = enrolledProducts;

    if (!honeycombEnrollment.some((p) => p.productSku === newEnrollment.productSku)) {
        honeycombEnrollment.concat([newEnrollment]);
    }

    sendToHoneycomb(sessionId, { enrollment: honeycombEnrollment });

    // Match sales-tools' createEnrollment: a decline is an expected outcome, so it isn't reported as an error.
    const isSuccess = progress === SaleProgress.Success;
    const isDeclined = progress === SaleProgress.Declined;
    const eventName = isSuccess ? "EnrollmentSuccess" : isDeclined ? "EnrollmentDeclined" : "EnrollmentFailure";

    trackFullStoryEvent(eventName, {
        productSku: enrolledProductSku ?? "",
        productOfferId: offerId,
        sessionId,
        enrollmentStatus: progress,
        statusOrigin: salesProvider,
    });

    endDynatraceAction("SaleEnrollment", isSuccess || isDeclined ? undefined : { taskId: offerId, error: eventName });

    if (isPrimaryProductInProgress(sessionId)) {
        updateIntegratedStep(sessionId, "SuggestedOfferStep");
    }
}

export function getProvider(provider: SalesProvider) {
    switch (provider) {
        case SalesProvider.ASP:
            return "ASP";
        case SalesProvider.EXPERT_SALES:
            return "EXPERTSALES";
        default:
            return undefined;
    }
}
